import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { 
  DecisionOption, 
  DecisionCriteria, 
  AnalysisStep, 
  AnalysisResult, 
  CausalChainNode, 
  ContextProfile, 
  MultiOptionAnalysis,
  DecisionAnalysisState 
} from '../types'
import { AnalysisHistoryService } from '../services/analysisHistoryService'

export const useDecisionAnalysisStore = create<DecisionAnalysisState>()(
  persist(
    (set, _get) => ({
      // Initial state
      decisionTitle: '',
      decisionDescription: '',
      options: [],
      criteria: [],
      currentStep: 1,
      isAnalyzing: false,
      analysisResult: null,
      analysisSteps: [],
      currentAnalysisStep: '',
      decisionHistory: [],
      
      // 新增状态
      analysisType: 'pros-cons',
      contextProfiles: [],
      activeProfileId: null,
      causalChainResult: null,
      multiOptionResult: null,

      // 现有Actions
      setDecisionTitle: (title: string) => set({ decisionTitle: title }),
      
      setDecisionDescription: (description: string) => set({ decisionDescription: description }),
      
      addOption: () => {
        const newOption: DecisionOption = {
          id: Date.now().toString(),
          text: '',
          pros: [],
          cons: [],
          score: 0
        }
        set((state) => ({ options: [...state.options, newOption] }))
      },
      
      removeOption: (id: string) => {
        set((state) => ({ 
          options: state.options.filter(option => option.id !== id) 
        }))
      },
      
      updateOption: (id: string, field: keyof DecisionOption, value: any) => {
        set((state) => ({
          options: state.options.map(option => 
            option.id === id ? { ...option, [field]: value } : option
          )
        }))
      },
      
      addCriteria: () => {
        const newCriteria: DecisionCriteria = {
          id: Date.now().toString(),
          name: '',
          weight: 1,
          description: ''
        }
        set((state) => ({ criteria: [...state.criteria, newCriteria] }))
      },
      
      removeCriteria: (id: string) => {
        set((state) => ({ 
          criteria: state.criteria.filter(c => c.id !== id) 
        }))
      },
      
      updateCriteria: (id: string, field: keyof DecisionCriteria, value: any) => {
        set((state) => ({
          criteria: state.criteria.map(c => 
            c.id === id ? { ...c, [field]: value } : c
          )
        }))
      },
      
      setCurrentStep: (step: number) => set({ currentStep: step }),
      
      setIsAnalyzing: (analyzing: boolean) => set({ isAnalyzing: analyzing }),
      
      setAnalysisResult: (result: AnalysisResult | null) => set({ analysisResult: result }),
      
      initializeAnalysisSteps: () => {
        const steps: AnalysisStep[] = [
          { id: 'step1', title: 'Data Collection', description: 'Gather all relevant data for the decision.', status: 'pending' },
          { id: 'step2', title: 'Criteria Definition', description: 'Define the key criteria for evaluating options.', status: 'pending' },
          { id: 'step3', title: 'Option Generation', description: 'Generate potential options for the decision.', status: 'pending' },
          { id: 'step4', title: 'Option Evaluation', description: 'Evaluate each option based on defined criteria.', status: 'pending' },
          { id: 'step5', title: 'Risk Assessment', description: 'Assess potential risks and mitigation strategies.', status: 'pending' },
          { id: 'step6', title: 'Conclusion', description: 'Draw a conclusion and recommend the best option.', status: 'pending' },
        ];
        set({ analysisSteps: steps });
        set({ currentAnalysisStep: 'step1' });
      },

      initializeCausalChainSteps: () => {
        const steps: AnalysisStep[] = [
          { id: 'step1', title: 'Decision Analysis', description: 'Understanding your decision structure and identifying the key factors that will influence outcomes.', status: 'pending' },
          { id: 'step2', title: 'Option Mapping', description: 'Mapping out all possible options and exploring how they connect to different scenarios.', status: 'pending' },
          { id: 'step3', title: 'Causal Chain Building', description: 'Building the causal chain showing how each decision leads to specific outcomes and consequences.', status: 'pending' },
          { id: 'step4', title: 'Probability Assessment', description: 'Calculating the likelihood of each outcome occurring based on various factors and conditions.', status: 'pending' },
          { id: 'step5', title: 'Impact Analysis', description: 'Evaluating the positive, negative, and neutral impacts of each outcome on your goals and situation.', status: 'pending' },
          { id: 'step6', title: 'Chain Visualization', description: 'Creating the final interactive causal chain visualization for easy understanding and exploration.', status: 'pending' },
        ];
        set({ analysisSteps: steps });
        set({ currentAnalysisStep: 'step1' });
      },

      initializeContextAnalysisSteps: () => {
        const steps: AnalysisStep[] = [
          { id: 'step1', title: 'Context Analysis', description: 'Analyzing your personal context and background.', status: 'pending' },
          { id: 'step2', title: 'Profile Integration', description: 'Integrating your profile preferences and priorities.', status: 'pending' },
          { id: 'step3', title: 'Option Evaluation', description: 'Evaluating options based on your personal context.', status: 'pending' },
          { id: 'step4', title: 'Personalized Scoring', description: 'Scoring options according to your preferences.', status: 'pending' },
          { id: 'step5', title: 'Recommendation Generation', description: 'Generating personalized recommendations.', status: 'pending' },
        ];
        set({ analysisSteps: steps });
        set({ currentAnalysisStep: 'step1' });
      },

      initializeMultiOptionAnalysisSteps: () => {
        const steps: AnalysisStep[] = [
          { id: 'step1', title: 'Option Analysis', description: 'Analyzing each option individually.', status: 'pending' },
          { id: 'step2', title: 'Criteria Evaluation', description: 'Evaluating options against each criterion.', status: 'pending' },
          { id: 'step3', title: 'Comparison Matrix', description: 'Building comparison matrix for all options.', status: 'pending' },
          { id: 'step4', title: 'Scoring Calculation', description: 'Calculating weighted scores for each option.', status: 'pending' },
          { id: 'step5', title: 'Ranking Analysis', description: 'Ranking options and generating recommendations.', status: 'pending' },
        ];
        set({ analysisSteps: steps });
        set({ currentAnalysisStep: 'step1' });
      },

      updateAnalysisStep: (stepId: string, status: AnalysisStep['status']) => {
        set((state) => ({
          analysisSteps: state.analysisSteps.map(step =>
            step.id === stepId ? { ...step, status } : step
          )
        }));
      },

      setCurrentAnalysisStep: (stepId: string) => {
        set({ currentAnalysisStep: stepId });
      },
      
      resetDecision: () => set({
        decisionTitle: '',
        decisionDescription: '',
        options: [],
        criteria: [],
        currentStep: 1,
        isAnalyzing: false,
        analysisResult: null,
        analysisSteps: [],
        currentAnalysisStep: '',
        causalChainResult: null,
        multiOptionResult: null,
      }),
      
      setOptions: (options: DecisionOption[]) => set({ options }),
      setCriteria: (criteria: DecisionCriteria[]) => set({ criteria }),
      
      addToHistory: async (decision: any) => {
        try {
          const historyData = {
            title: decision.title,
            description: decision.description,
            analysis_type: decision.analysisType || 'pros-cons',
            options: decision.options,
            criteria: decision.criteria,
            result: decision.result,
            context_profile: decision.contextProfile
          }
          
          const newHistory = await AnalysisHistoryService.createHistory(historyData)
          
          set((state) => ({ 
            decisionHistory: [{
              ...newHistory,
              createdAt: new Date(newHistory.created_at)
            }, ...state.decisionHistory]
          }))
        } catch (error) {
          console.error('Error adding to history:', error)
          // Fallback to local storage if database fails
          const historyEntry = {
            id: Date.now().toString(),
            title: decision.title,
            description: decision.description,
            options: decision.options,
            criteria: decision.criteria,
            result: decision.result,
            createdAt: new Date()
          }
          set((state) => ({ 
            decisionHistory: [historyEntry, ...state.decisionHistory.slice(0, 9)]
          }))
        }
      },
      
      clearHistory: () => set({ decisionHistory: [] }),
      
      // Database related actions
      loadHistoryFromDatabase: async () => {
        try {
          const history = await AnalysisHistoryService.getUserHistory()
          set({ decisionHistory: history.map(h => ({
            ...h,
            createdAt: new Date(h.created_at)
          })) })
        } catch (error) {
          console.error('Error loading history from database:', error)
        }
      },
      
      loadHistoryByType: async (analysisType: string) => {
        try {
          const history = await AnalysisHistoryService.getHistoryByType(analysisType)
          set({ decisionHistory: history.map(h => ({
            ...h,
            createdAt: new Date(h.created_at)
          })) })
        } catch (error) {
          console.error('Error loading history by type:', error)
        }
      },
      
      deleteHistoryItem: async (id: string) => {
        try {
          await AnalysisHistoryService.deleteHistory(id)
          set((state) => ({
            decisionHistory: state.decisionHistory.filter(h => h.id !== id)
          }))
        } catch (error) {
          console.error('Error deleting history item:', error)
        }
      },

      // 新增Actions
      setAnalysisType: (type: string) => set({ analysisType: type }),
      
      addContextProfile: () => {
        const newProfile: ContextProfile = {
          id: Date.now().toString(),
          name: 'New Profile',
          description: '',
          personalInfo: {},
          preferences: {
            riskTolerance: 'medium',
            timeHorizon: 'medium',
            priorities: []
          }
        }
        set((state) => ({ 
          contextProfiles: [...state.contextProfiles, newProfile],
          activeProfileId: newProfile.id
        }))
      },
      
      updateContextProfile: (id: string, profile: Partial<ContextProfile>) => {
        set((state) => ({
          contextProfiles: state.contextProfiles.map(p => 
            p.id === id ? { ...p, ...profile } : p
          )
        }))
      },
      
      removeContextProfile: (id: string) => {
        set((state) => ({ 
          contextProfiles: state.contextProfiles.filter(p => p.id !== id),
          activeProfileId: state.activeProfileId === id ? null : state.activeProfileId
        }))
      },
      
      setActiveProfile: (id: string | null) => set({ activeProfileId: id }),
      
      setCausalChainResult: (result: CausalChainNode | null) => set({ causalChainResult: result }),
      
      setMultiOptionResult: (result: MultiOptionAnalysis | null) => set({ multiOptionResult: result })
    }),
    {
      name: 'decision-analysis-storage',
      partialize: (state) => ({
        decisionHistory: state.decisionHistory,
        contextProfiles: state.contextProfiles,
        activeProfileId: state.activeProfileId
      })
    }
  )
) 